import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Lock, User, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, formData);
      
      if (response.data.access_token) {
        localStorage.setItem('adminToken', response.data.access_token);
        localStorage.setItem('adminTokenExpiry', Date.now() + response.data.expires_in * 1000);
        toast.success('Login successful!');
        navigate('/admin');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.detail || 'Invalid username or password');
      toast.error('Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F1A] flex items-center justify-center p-4">
      {/* Background effects */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(#00E5FF 1px, transparent 1px), linear-gradient(90deg, #00E5FF 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>
      </div>
      <div className="absolute top-20 right-20 w-96 h-96 bg-[#00E5FF] rounded-full blur-[150px] opacity-10"></div>
      <div className="absolute bottom-20 left-20 w-80 h-80 bg-[#1DE9B6] rounded-full blur-[150px] opacity-10"></div>

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_talent-solutions-10/artifacts/02y8uuas_IMG_9047.JPG" 
            alt="CloudXAI Logo" 
            className="h-16 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-white font-['Space_Grotesk']">Admin Login</h1>
          <p className="text-[#9CA3AF] text-sm mt-2">Enter your credentials to access the dashboard</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="bg-[#111827] border border-[#00E5FF]/30 rounded-2xl p-8 shadow-2xl shadow-[#00E5FF]/10">
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center text-red-400">
              <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-[#9CA3AF] mb-2">Username</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  className="w-full pl-12 pr-4 py-3 bg-[#0B0F1A] border border-[#00E5FF]/20 rounded-lg text-white placeholder-[#6B7280] focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF] transition-all"
                  placeholder="Enter username"
                  data-testid="username-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#9CA3AF] mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full pl-12 pr-12 py-3 bg-[#0B0F1A] border border-[#00E5FF]/20 rounded-lg text-white placeholder-[#6B7280] focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF] transition-all"
                  placeholder="Enter password"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full mt-6 bg-[#00E5FF] hover:bg-[#1DE9B6] text-[#0B0F1A] font-semibold py-4 rounded-lg shadow-lg shadow-[#00E5FF]/40 hover:shadow-[#00E5FF]/60 transition-all duration-300"
            data-testid="login-button"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <span className="animate-spin w-5 h-5 border-2 border-[#0B0F1A] border-t-transparent rounded-full mr-2"></span>
                Signing in...
              </span>
            ) : (
              'Sign In'
            )}
          </Button>

          <p className="text-center text-[#6B7280] text-sm mt-6">
            <a href="/" className="text-[#00E5FF] hover:text-[#1DE9B6] transition-colors">
              ← Back to Website
            </a>
          </p>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
